import React, { FC, useRef } from 'react';
import { DocType, ImageContext, ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { ColorFunction } from '@styles/colors/types';
import { StyleProp, StyleSheet, Text, TextStyle, View, ViewStyle } from 'react-native';
import { IconConfig, ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { isPhone, isWeb } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import { useManageDocTypeButton } from './hooks/useManageDocTypeButton';
import i18n from 'i18n-js';
import DocTypeModal from './DocTypeModal';
import ModalWrapper from '@components/modal/ModalWrapper';
import { modalBg, TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface DocTypeButtonProps {
  img?: ImageDocumentStoreItem | null;
  images: ImageDocumentStoreItem[];
  C: ColorFunction;
  containerStyles?: StyleProp<ViewStyle>;
  textStyles?: StyleProp<TextStyle>;
  shouldModalPositionBeRelatedToButton?: boolean;
  shouldCenterModal?: boolean;
  leftIconStyles?: Partial<IconConfig>;
  rightIconStyles?: Partial<IconConfig>;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  configStyles?: StyleProp<ViewStyle>;
  onSuccessDocTypeChange?: (newDocType: DocType, prevDocType: DocType) => void;
}

const DocTypeButton: FC<DocTypeButtonProps> = ({
  images,
  img,
  C,
  containerStyles,
  textStyles,
  leftIconStyles,
  rightIconStyles,
  configStyles = {},
  onSuccessDocTypeChange,
  shouldModalPositionBeRelatedToButton = false,
  shouldCenterModal = true,
  scale = initialScale,
  font = initialFont,
}) => {
  const docTypeButtonRef = useRef<View | null>(null);

  const {
    colorWithDocType,
    docTypeBorderRadius,
    docTypeButtonFont,
    docTypeHeight,
    docTypeModalOpened,
    docTypePaddingHorizontal,
    selectedImage,
    // docTypeCategoriesLoading,
    setDocTypeModalOpened,
    imgLoaded,
    onChangeDocType,
    shouldUseWebIconStyles,
    docTypeExist,
    svgBasedOnDocType,
    docType,
    docTypeButtonMeasurements,
    onCloseModal,
  } = useManageDocTypeButton({
    docTypeButtonRef,
    images,
    img,
    onSuccessDocTypeChange,
  });

  if (!selectedImage) return null;

  return (
    <View
      ref={docTypeButtonRef}
      style={{
        zIndex: 10000,
        opacity: !imgLoaded ? 0 : 1,
      }}
    >
      <TouchableElement
        testID={TEST_IDS.documentsHeader.docTypeButton}
        onPress={() => {
          setDocTypeModalOpened(true);
        }}
      >
        {({ boxStyle }) => {
          return (
            <View
              style={[
                boxStyle,
                {
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: scale(docTypeHeight),
                  borderRadius: scale(docTypeBorderRadius),
                  backgroundColor: C(
                    docTypeModalOpened
                      ? 'Button/Light/Tertiary pressed'
                      : docTypeExist && shouldUseWebIconStyles
                      ? 'transparent'
                      : !docTypeExist && shouldUseWebIconStyles
                      ? 'Background/Light/Field lighter'
                      : 'Gray/Light/5'
                  ),
                  borderWidth: StyleSheet.hairlineWidth,
                  borderColor: docTypeModalOpened
                    ? 'transparent'
                    : C(docTypeExist ? 'Hairline/Light/No trans [mobile]' : 'Hairline/Light/Transpar [mobile]'),
                  borderStyle: docTypeExist ? 'solid' : 'dashed',
                  gap: scale(6),
                  alignSelf: 'flex-start',
                  paddingHorizontal: scale(docTypePaddingHorizontal),
                },
                containerStyles,
              ]}
            >
              {svgBasedOnDocType && !isPhone && (
                <View style={{ paddingRight: scale(3.5) }}>
                  <Svg {...svgBasedOnDocType} color={C(colorWithDocType)} {...leftIconStyles} />
                </View>
              )}
              <Text
                style={[
                  font[docTypeButtonFont as keyof typeof font],
                  {
                    color: C(colorWithDocType),
                  },
                  textStyles,
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {i18n.t(docType || 'Select Document Type…')}
              </Text>
              {docTypeExist && (
                <Svg
                  icon={isPhone ? 'chevronBottomLight' : 'chevronRegular'}
                  width={scale(14)}
                  height={scale(14)}
                  color={C(rightIconStyles?.color ?? colorWithDocType)}
                  {...(docTypeModalOpened ? { transform: 'rotate(180)' } : {})}
                  {...{ rightIconStyles }}
                />
              )}
            </View>
          );
        }}
      </TouchableElement>

      {selectedImage && docTypeModalOpened && (
        <ModalWrapper backdropColor={'Overlay/Light/Dim strong'} isVisible={docTypeModalOpened} onBackdropPress={onCloseModal} C={C}>
          <View
            style={[
              {
                position: shouldCenterModal ? 'relative' : 'absolute',
                top:
                  !shouldCenterModal && shouldModalPositionBeRelatedToButton
                    ? docTypeButtonMeasurements.y + docTypeButtonMeasurements.height + scale(5)
                    : !shouldCenterModal
                    ? scale(70)
                    : undefined,
                alignSelf: shouldCenterModal ? 'center' : undefined,
                width: scale(480),
                backgroundColor: C(modalBg),
                borderRadius: scale(9),
              },
              !shouldCenterModal && { left: scale(18), right: scale(18) },
              configStyles,
            ]}
          >
            <DocTypeModal selectedValue={selectedImage?.documentTag} change={onChangeDocType} onClose={onCloseModal} />
          </View>
        </ModalWrapper>
      )}
    </View>
  );
};

export default DocTypeButton;
